/*
 * File: AccountCard.tsx
 * Project: a-a
 * File Created: Tuesday, 19th November 2024 8:03:45 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 21st April 2026 9:38:02 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React from 'react';
import CustomCard from '../../../CustomCard/CustomCard';
import zeplinColor from '../../../../../theme/Color';
import { Box, Chip, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import { LightTooltip } from '../../../CustomTooltip/CustomTooltip';
import { Link, useNavigate } from 'react-router-dom';
import formatCurrency from '../../../../../helper/short-currency';
import { ReactComponent as CalendarIcon } from '../../../../../assets/action-tooltip-calendar-light.svg';
import { ReactComponent as AlarmIcon } from '../../../../../assets/action-tooltip-alarm-light.svg';
import { ReactComponent as EmojiIcon } from '../../../../../assets/action-tooltip-emoji-light.svg';
import { ReactComponent as DealIcon } from '../../../../../assets/action-tooltip-deal-light.svg';
import styles from './AccountCard.module.css';
const AccountCard = ({
  data,
  index,
  loading = false,
  path,
}: {
  data: any;
  index: number;
  loading?: boolean;
  path?: string;
}) => {
  // console.log(data);
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    overdueTasks,
    hasLongTimeNoContact,
    inStageToLong,
    dealsLostMonth,
    hasTooManyDealsLostMonth,
    dealsLostQuarter,
    hasTooManyDealsLostQuarter,
  } = data;
  const accountName = data?.accountName?.split(' ');

  return (
    <Box
      sx={{
        // marginTop: '16px',
        borderRadius: '14px',
        padding: '12px',
        backgroundColor: zeplinColor.Neutral3,
        border: `0px solid ${zeplinColor.Neutral6}`,
        display: 'flex',
        gap: '12px',
        // flex: 1,
        width: '100%',
        height: '98px',
        cursor: 'pointer',
        '&:hover': {
          // backgroundColor: zeplinColor.Neutral4,
          border: `1px solid ${zeplinColor.BasicBlack}`,
        },
        // alignItems: 'center',
        // flexDirection: 'column',
        // justifyContent: 'space-between',
      }}
      onClick={() => {
        if (path) {
          navigate(`${path}/${data?.accountId}`, {
            state: {
              client: data,
            },
          });
          sessionStorage.setItem('currentTab', '0');
        }
      }}
    >
      {/* <img src='/assets/user-action.svg' width={68} height={68} style={{ objectFit: 'fill' }} /> */}
      {loading ? (
        <Skeleton variant='rounded' width={68} height={68} />
      ) : data?.avatar ? (
        <img
          src={data?.avatar}
          width={68}
          height={68}
          style={{ objectFit: 'cover', borderRadius: '14px' }}
        />
      ) : (
        <Box
          sx={{
            borderRadius: '14px',
            // padding: '10px',
            width: '68px',
            height: '68px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            gap: '10px',
            backgroundColor:
              (index + 1) % 3 === 1
                ? zeplinColor.Blue3
                : index % 3 === 2
                  ? zeplinColor.Green1
                  : zeplinColor.Brand1,
          }}
        >
          <Typography variant='display5' color={zeplinColor.Neutral1} sx={{ textAlign: 'center' }}>
            {accountName?.map((value: any, index: number) =>
              index === 0 || index === accountName?.length - 1 ? value[0] : '',
            )}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          flex: 1,

          //   padding: '2px',
          //   alignItems: 'center',
          //   justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            // height: '68px',
            alignItems: 'center',
            gap: '24px',
            textAlign: 'left',
          }}
        >
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                {data?.accountName}
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
          >
            <Typography
              variant='responsiveBody3'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%', // Adjust based on container
                // width: { xs: '350px', xl: '450px' }, // Adjust width for responsiveness
              }}
            >
              {/* {data?.accountName?.slice(0, 20)} {data?.accountName?.length > 20 && '...'} */}
              {data?.accountName ?? 'N/A'}
            </Typography>
          </LightTooltip>

          <Box
            sx={{
              display: 'flex',
              // justifyContent: 'space-between',
              // height: '68px',
              alignItems: 'center',
              gap: '8px',
              textAlign: 'left',
            }}
          >
            <LightTooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {data?.overdueTasks ? (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* <img src='/assets/action-tooltip-calendar-light.svg' width={18} height={18} /> */}
                      <CalendarIcon width={18} height={18} />{' '}
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                        {data?.overdueTasks} tasks remain overdue.
                      </Typography>
                    </Box>
                  ) : (
                    ''
                  )}
                  {data?.hasLongTimeNoContact ? (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* <img src='/assets/action-tooltip-alarm-light.svg' width={18} height={18} /> */}
                      <AlarmIcon width={18} height={18} />
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                        No activity has occurred for the past 30 days.
                      </Typography>
                    </Box>
                  ) : (
                    ''
                  )}
                  {data?.dealsLostMonth ? (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* <img src='/assets/action-tooltip-emoji-light.svg' width={18} height={18} /> */}
                      <EmojiIcon width={18} height={18} />
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                        {data?.dealsLostMonth} deals were lost in the month.
                      </Typography>
                    </Box>
                  ) : (
                    ''
                  )}
                  {data?.dealsLostQuarter ? (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* <img src='/assets/action-tooltip-emoji-light.svg' width={18} height={18} /> */}
                      <EmojiIcon width={18} height={18} />
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                        {data?.dealsLostQuarter} deals were lost in the quarter.
                      </Typography>
                    </Box>
                  ) : (
                    ''
                  )}
                  {data?.inStageToLong ? (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      {/* <img src='/assets/action-tooltip-deal-light.svg' width={18} height={18} /> */}
                      <DealIcon width={18} height={18} />
                      <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                        Deals are stuck in the same stages for the past 30 days.
                      </Typography>
                    </Box>
                  ) : (
                    ''
                  )}
                  {/* <Box sx={{ display: 'flex', gap: '8px' }}>
                    <img src='/assets/action-tooltip-revenue.svg' width={18} height={18} />
                    <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                      Underperforming in revenue goals
                    </Typography>
                  </Box> */}
                </Box>
              }
              placement='bottom-end'
              style={{
                backgroundColor: zeplinColor.BasicBlack,
                borderRadius: '14px',
                padding: '16px',
                // zIndex: 0,
              }}
              // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
            >
              {loading ? (
                <Skeleton variant='rounded' width={24} height={24} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {/* <img
                  src='/assets/overdue-icon.svg'
                  width={24}
                  height={24}
                  style={{ objectFit: 'fill' }}
                /> */}

                  {[
                    overdueTasks,
                    hasLongTimeNoContact,
                    inStageToLong,
                    hasTooManyDealsLostMonth,
                    hasTooManyDealsLostQuarter,
                  ].filter((val) => val).length > 2 ? (
                    <img
                      src='/assets/sad-02.svg'
                      className={styles['emoji']}
                      style={{ objectFit: 'fill' }}
                    />
                  ) : [
                      overdueTasks,
                      hasLongTimeNoContact,
                      inStageToLong,
                      hasTooManyDealsLostMonth,
                      hasTooManyDealsLostQuarter,
                    ].filter((val) => val).length == 2 ? (
                    <img
                      src='/assets/sad-01.svg'
                      className={styles['emoji']}
                      style={{ objectFit: 'fill' }}
                    />
                  ) : (
                    <img
                      src='/assets/neutral.svg'
                      className={styles['emoji']}
                      style={{ objectFit: 'fill' }}
                    />
                  )}
                </Box>
              )}
            </LightTooltip>
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  Click to open
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              {loading ? (
                <Skeleton variant='rounded' width={24} height={24} />
              ) : (
                <Link
                  to={data?.accountURL}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <img
                    src='/assets/arrow-link.svg'
                    className={styles['emoji']}
                    alt='link to actionable-aacount'
                  />
                </Link>
              )}
            </LightTooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant='responsiveH1' style={{ textAlign: 'left' }}>
                ${formatCurrency(data?.wonDeals)}
              </Typography>
            )}
            <Typography
              variant='responsiveLabel1'
              color={zeplinColor.Neutral8}
              sx={{ textWrap: 'nowrap', whiteSpace: 'nowrap' }}
            >
              Closed Deals
            </Typography>
          </Box>
          {/* <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
          /> */}
          <Box
            sx={{
              height: '20px',
              width: '1px',
              backgroundColor: zeplinColor.Neutral5,
              display: 'inline-block',
              marginX: '4px', // Add some margin on both sides
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant='responsiveH1' style={{ textAlign: 'left' }}>
                ${formatCurrency(data?.openDeals)}
              </Typography>
            )}
            <Typography
              variant='responsiveLabel1'
              color={zeplinColor.Neutral8}
              sx={{ textWrap: 'nowrap', whiteSpace: 'nowrap' }}
            >
              Open Deals
            </Typography>
          </Box>
          {/* <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
          /> */}
          <Box
            sx={{
              height: '20px',
              width: '1px',
              backgroundColor: zeplinColor.Neutral5,
              display: 'inline-block',
              marginX: '4px', // Add some margin on both sides
            }}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100%',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant='responsiveH1' style={{ textAlign: 'left' }}>
                {data?.openTasks}
              </Typography>
            )}
            <Typography
              variant='responsiveLabel1'
              color={zeplinColor.Neutral8}
              sx={{ textWrap: 'nowrap', whiteSpace: 'nowrap' }}
            >
              Open Tasks
            </Typography>
          </Box>
          {/* <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, borderWidth: 1, height: '20px' }}
          /> */}
        </Box>
      </Box>
    </Box>
  );
};

export default AccountCard;
